import React from "react";
import Skeleton from "../ui/Skeleton";

interface CampaignLeadsSkeletonProps {
  viewMode?: "list" | "kanban";
}

const CampaignLeadsSkeleton: React.FC<CampaignLeadsSkeletonProps> = ({
  viewMode = "list",
}) => {
  return (
    <div className="space-y-4 animate-pulse px-1">
      {/* Performance Overview (4 Cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="relative bg-white border border-purple-200 rounded-xl p-4 shadow-xs flex flex-col gap-3 overflow-hidden"
          >
            {/* Accent bar skeleton */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-purple-100" />
            <div className="flex items-start justify-between pt-0.5">
              <div className="space-y-1.5 flex flex-col min-w-0">
                <Skeleton variant="text" width="60px" height="0.625rem" />
                <Skeleton
                  variant="text"
                  width="40px"
                  height="1.5rem"
                  className="mt-1.5"
                />
              </div>
              <Skeleton
                variant="rectangular"
                width="36px"
                height="36px"
                className="rounded-lg shrink-0"
              />
            </div>
            {/* Footer Skeleton */}
            <div className="mt-auto flex items-center justify-between gap-1 px-2 py-1.5 rounded-lg bg-gray-50">
              <Skeleton variant="text" width="80px" height="0.6rem" />
            </div>
          </div>
        ))}
      </div>

      {/* Filter Bar Skeleton */}
      <div className="sticky top-0 z-30 -mx-4 px-4 py-2 mb-4 bg-white/95 backdrop-blur-md border-b border-purple-200 flex flex-col gap-2">
        {/* Row 1: Search and Actions */}
        <div className="flex flex-row flex-wrap gap-2 items-center w-full">
          <div className="flex-1 min-w-[200px]">
            <Skeleton
              variant="rectangular"
              width="100%"
              height="2rem"
              className="rounded-lg sm:h-9"
            />
          </div>
          <div className="w-[100px] shrink-0">
            <Skeleton
              variant="rectangular"
              width="100%"
              height="2rem"
              className="rounded-lg sm:h-9"
            />
          </div>
          {viewMode === "kanban" && (
            <div className="w-[120px] shrink-0">
              <Skeleton
                variant="rectangular"
                width="100%"
                height="2rem"
                className="rounded-lg sm:h-9"
              />
            </div>
          )}
          <div className="w-[100px] shrink-0">
            <Skeleton
              variant="rectangular"
              width="100%"
              height="2rem"
              className="rounded-lg sm:h-9"
            />
          </div>
        </div>

        {/* Row 2: Filters (5 items in auto-fit grid) */}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2 w-full">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="w-full">
              <Skeleton
                variant="rectangular"
                width="100%"
                height="2rem"
                className="rounded-lg sm:h-9"
              />
            </div>
          ))}
        </div>
      </div>

      {viewMode === "list" ? (
        // Table Area Skeleton
        <div className="bg-white border border-purple-200 rounded-xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 border-b border-purple-200">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <th key={i} className="px-4 py-3">
                      <Skeleton variant="text" width="80px" height="0.625rem" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td className="px-4 py-3" colSpan={8}>
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-4">
                          <Skeleton
                            variant="rectangular"
                            width="40px"
                            height="40px"
                            className="rounded-2xl"
                          />
                          <div className="space-y-2">
                            <Skeleton
                              variant="text"
                              width="150px"
                              height="0.9rem"
                            />
                            <Skeleton
                              variant="text"
                              width="100px"
                              height="0.6rem"
                              className="opacity-50"
                            />
                          </div>
                        </div>
                        <Skeleton
                          variant="rectangular"
                          width="80px"
                          height="1.5rem"
                          className="rounded-xl opacity-40"
                        />
                        <Skeleton
                          variant="rectangular"
                          width="100px"
                          height="1.5rem"
                          className="rounded-xl opacity-40"
                        />
                        <Skeleton variant="text" width="60px" height="1.5rem" />
                        <div className="flex gap-2">
                          <Skeleton
                            variant="rectangular"
                            width="120px"
                            height="2.5rem"
                            className="rounded-xl"
                          />
                          <Skeleton
                            variant="rectangular"
                            width="40px"
                            height="2.5rem"
                            className="rounded-xl"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Kanban Board Skeleton
        <div className="flex gap-4 overflow-x-auto pb-4 kanban-scroll h-[calc(100vh-270px)] min-h-[400px]">
          {[1, 2, 3, 4, 5].map((col) => (
            <div
              key={col}
              className="flex flex-col min-w-[240px] flex-1 max-w-[310px]"
            >
              <Skeleton
                variant="rectangular"
                width="100%"
                height={44}
                className="rounded-2xl mb-3"
              />
              <div className="rounded-2xl border-2 border-dashed border-purple-100 p-2 space-y-2 flex-1">
                {[1, 2].map((c) => (
                  <Skeleton
                    key={c}
                    variant="rectangular"
                    width="100%"
                    height={140}
                    className="rounded-2xl"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignLeadsSkeleton;
